import {
  CalendarOutlined,
  HistoryOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Empty,
  message,
  Popover,
  Space,
  Spin,
  Timeline,
  Tooltip,
} from 'antd';
import moment from 'moment';
import React, { useState } from 'react';

import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';

export type UserActivityItem = {
  updated_at: string;
  created_at: string;
  id: number;
  user_id: number;
  table_name: string;
  value_type: string;
  row_id: number;
  old_value: string;
  new_value: string;
  user: {
    id: number;
    username: string;
    phone_number: string;
    full_name: string;
    role: string;
    team: string;
  };
};

const OrderDoDateHistory = (props: { orderId: number; date: string }) => {
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const histories = useQuery<{
    loading: boolean;
    items: UserActivityItem[];
    page: number;
    per_page: number;
    total: number;
  }>(
    ['order-do-date-history', props.orderId, visible, page],
    async () => {
      if (!props.orderId || !visible) {
        return {
          loading: true,
          items: [],
          page,
          per_page: pageSize,
          total: 0,
        };
      }

      try {
        const resp = await fetchAPI('user/activity', {
          payload: {
            table_name: 'order',
            value_type: 'update_do_date',
            row_id: props.orderId,
            page,
            per_page: pageSize,
          },
        });

        return { ...resp, loading: false };
      } catch (error) {
        message.error('Failed get history');
        message.error(error.message || 'Error');

        return {
          loading: false,
          items: [],
          page,
          per_page: pageSize,
          total: 0,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        loading: true,
        items: [],
        page,
        per_page: pageSize,
        total: 0,
      },
    }
  );

  if (!props.date) {
    return null;
  }

  return (
    <Popover
      visible={visible}
      onVisibleChange={vis => setVisible(vis)}
      title="DO Date History"
      trigger={'click'}
      placement="rightTop"
      content={
        <Spin spinning={histories.data.loading || histories.isLoading}>
          {!histories.data.items.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Timeline style={{ maxWidth: 230 }}>
              {histories.data.items.map((item, idx, arr) => {
                // because, item.old_value is not correct
                const nextItem = arr[idx + 1];
                return (
                  <Timeline.Item dot={<CalendarOutlined />} style={{}}>
                    Change Date from{' '}
                    <b>
                      {nextItem?.new_value
                        ? moment(nextItem?.new_value).format('DD MMM YYYY')
                        : 'nothing'}
                    </b>{' '}
                    to <b>{moment(item.new_value).format('DD MMM YYYY')}</b>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '11px',
                        opacity: '0.7',
                      }}
                    >
                      {formatUTCTimeToLocal(
                        item?.created_at,
                        'DD MMM YYYY HH:mm'
                      )}
                      <div style={{ fontSize: '11px' }}>
                        <UserOutlined /> By {item?.user?.full_name}
                      </div>
                    </span>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}

          {(page > 1 || histories.data.total > page * pageSize) && (
            <Space>
              <Button
                icon={<LeftOutlined />}
                onClick={() => setPage(p => p - 1)}
                disabled={page <= 1}
              />
              <Button
                icon={<RightOutlined />}
                onClick={() => setPage(p => p + 1)}
                disabled={histories.data.total <= page * pageSize}
              />
            </Space>
          )}
        </Spin>
      }
    >
      <Tooltip title="See History" placement="top">
        <HistoryOutlined style={{ cursor: 'pointer' }} />
      </Tooltip>
    </Popover>
  );
};

export default OrderDoDateHistory;
